import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Globe2, HeartPulse, Gauge, Filter, Timer, FileCheck2, ShieldAlert, Coins, TrendingUp, TrendingDown, Minus, Award, Rocket, Sun, Sunrise } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, YAxis, Tooltip } from "recharts";

// Milestone metadata — copy + icon + colour cue for each celebratable pattern the
// backend detects. Kept as data so a new milestone is a 3-line change.
const MILESTONES = {
  steady_improvement: { label: "5+ sessions · improving", icon: Award, tone: "bg-secondary/15 text-secondary border-secondary/30" },
  big_leap: { label: "Big leap · +3 points", icon: Rocket, tone: "bg-primary/15 text-primary border-primary/25" },
  climb_from_low: { label: "Climb from low", icon: Sunrise, tone: "bg-accent/40 text-[#3D405B] border-accent/60" },
  sustained_high: { label: "Sustained high", icon: Sun, tone: "bg-secondary/25 text-secondary border-secondary/40" },
};

// ---------- Shared helpers ----------
const fmtSeconds = (s) => {
  if (s === null || s === undefined) return "—";
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  return `${(s / 3600).toFixed(1)}h`;
};
const fmtAmount = (minor, curr = "ZAR") => {
  // Yoco/Stripe amounts are in minor units (cents). Rands = amount/100.
  const major = (minor || 0) / 100;
  return new Intl.NumberFormat(undefined, { style: "currency", currency: curr }).format(major);
};

function useReport(path, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(path)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) toast.error("Could not load report"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, deps);
  return { data, loading };
}

function Card({ children, testid, className = "" }) {
  return <div className={`brand-card p-6 ${className}`} data-testid={testid}>{children}</div>;
}
function Stat({ label, value, tone = "default", testid }) {
  const toneClass = { primary: "text-primary", secondary: "text-secondary", danger: "text-destructive", muted: "text-[#7A7D96]", default: "text-[#3D405B]" }[tone];
  return (
    <div className="rounded-2xl border border-[#3D405B]/10 bg-white p-4" data-testid={testid}>
      <div className={`text-3xl font-extrabold ${toneClass}`}>{value ?? "—"}</div>
      <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#7A7D96] mt-1">{label}</div>
    </div>
  );
}

// ---------- Report a. Impact ----------
export function ImpactReport() {
  const [days, setDays] = useState(30);
  const { data, loading } = useReport(`/admin/reports/impact?days_back=${days}`, [days]);
  if (loading) return <div className="brand-card p-8 animate-pulse h-40" />;
  if (!data) return null;
  const hero = data.hero || {};
  return (
    <div className="space-y-4" data-testid="report-impact">
      <WindowSelect value={days} onChange={setDays} options={[7, 30, 90, 365]} testid="impact-window" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Lives touched" value={hero.lives_touched} tone="primary" testid="impact-lives" />
        <Stat label="Sessions" value={hero.sessions} tone="secondary" testid="impact-sessions" />
        <Stat label="Hours of care" value={hero.hours_of_care} tone="default" testid="impact-hours" />
        <Stat label="Counsellors active" value={hero.counsellors_active} tone="muted" testid="impact-counsellors" />
      </div>
      <Card testid="impact-geography">
        <div className="flex items-center gap-2 mb-3"><Globe2 size={16} className="text-primary" /><div className="font-extrabold text-[#3D405B]">Geographic reach</div></div>
        <div className="space-y-1.5">
          {(data.geography || []).map((g) => {
            const max = Math.max(1, ...(data.geography || []).map((x) => x.count));
            const pct = (g.count / max) * 100;
            return (
              <div key={g.province} className="flex items-center gap-2 text-sm" data-testid={`impact-geo-${g.province}`}>
                <div className="w-32 text-[#5F627D] truncate">{g.province}</div>
                <div className="flex-1 h-3 bg-muted/40 rounded-full overflow-hidden">
                  <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                </div>
                <div className="w-10 text-right font-extrabold text-[#3D405B]">{g.count}</div>
              </div>
            );
          })}
        </div>
      </Card>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card testid="impact-age">
          <div className="font-extrabold text-[#3D405B] mb-3">Age breakdown</div>
          {Object.entries(data.age_buckets || {}).map(([k, v]) => (
            <div key={k} className="flex justify-between text-sm py-1" data-testid={`impact-age-${k}`}>
              <span className="text-[#5F627D]">{k}</span>
              <span className="font-extrabold text-[#3D405B]">{v}</span>
            </div>
          ))}
        </Card>
        <Card testid="impact-mode">
          <div className="font-extrabold text-[#3D405B] mb-3">Session mode</div>
          <div className="flex items-center gap-4">
            <div className="flex-1 text-center">
              <div className="text-3xl font-extrabold text-primary">{data.mode?.online || 0}</div>
              <div className="text-xs text-[#7A7D96] mt-1">Online</div>
            </div>
            <div className="w-px h-16 bg-[#3D405B]/10" />
            <div className="flex-1 text-center">
              <div className="text-3xl font-extrabold text-secondary">{data.mode?.in_person || 0}</div>
              <div className="text-xs text-[#7A7D96] mt-1">In person</div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

// ---------- Report b. Wellness Outcomes ----------
export function WellnessOutcomesReport() {
  const [days, setDays] = useState(90);
  const { data, loading } = useReport(`/admin/reports/wellness-outcomes?days_back=${days}`, [days]);
  if (loading) return <div className="brand-card p-8 animate-pulse h-32" />;
  if (!data) return null;
  const total = (data.clients_improved || 0) + (data.clients_same || 0) + (data.clients_declined || 0);
  const pct = (n) => (total ? Math.round((n / total) * 100) : 0);
  return (
    <div className="space-y-4" data-testid="report-wellness">
      <WindowSelect value={days} onChange={setDays} options={[30, 90, 180, 365]} testid="wellness-window" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Clients tracked" value={data.clients_tracked} tone="default" />
        <Stat label="Improved" value={data.clients_improved} tone="secondary" testid="wellness-improved" />
        <Stat label="Same" value={data.clients_same} tone="muted" testid="wellness-same" />
        <Stat label="Declined" value={data.clients_declined} tone="danger" testid="wellness-declined" />
      </div>
      <Card testid="wellness-avg">
        <div className="flex items-center gap-2 mb-3"><HeartPulse size={16} className="text-primary" /><div className="font-extrabold text-[#3D405B]">Avg wellness delta</div></div>
        <div className="flex items-center gap-4">
          <div className={`text-5xl font-extrabold ${data.avg_delta > 0 ? "text-secondary" : data.avg_delta < 0 ? "text-destructive" : "text-[#3D405B]"}`}>
            {data.avg_delta > 0 ? "+" : ""}{data.avg_delta}
          </div>
          <div className="text-sm text-[#5F627D]">
            {data.avg_delta > 0 && <TrendingUp size={22} className="text-secondary inline" />}
            {data.avg_delta < 0 && <TrendingDown size={22} className="text-destructive inline" />}
            {data.avg_delta === 0 && <Minus size={22} className="text-[#7A7D96] inline" />}
            <span className="ml-2 align-middle">across {data.total_checkins} check-ins</span>
          </div>
        </div>
        {total > 0 && (
          <div className="mt-4 h-3 rounded-full bg-muted/40 overflow-hidden flex">
            <div className="bg-secondary" style={{ width: `${pct(data.clients_improved)}%` }} title={`Improved ${pct(data.clients_improved)}%`} />
            <div className="bg-[#7A7D96]/40" style={{ width: `${pct(data.clients_same)}%` }} title={`Same ${pct(data.clients_same)}%`} />
            <div className="bg-destructive" style={{ width: `${pct(data.clients_declined)}%` }} title={`Declined ${pct(data.clients_declined)}%`} />
          </div>
        )}
      </Card>
      {(data.client_series || []).length > 0 && (
        <Card testid="wellness-client-charts">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-extrabold text-[#3D405B]">Individual journeys</div>
              <div className="text-[11px] text-[#7A7D96] mt-0.5">Milestones first, then biggest improvement · showing top {data.client_series.length}</div>
            </div>
          </div>
          {/* Milestone summary — donor-friendly "wins" strip. Only shows keys with 1+. */}
          {data.milestone_totals && Object.values(data.milestone_totals).some((v) => v > 0) && (
            <div className="flex flex-wrap gap-2 mb-4" data-testid="wellness-milestone-totals">
              {Object.entries(data.milestone_totals).map(([key, count]) => {
                if (!count) return null;
                const m = MILESTONES[key];
                if (!m) return null;
                const Icon = m.icon;
                return (
                  <div key={key} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-extrabold border ${m.tone}`} data-testid={`milestone-total-${key}`}>
                    <Icon size={12} /> {count} × {m.label}
                  </div>
                );
              })}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="wellness-small-multiples">
            {(data.client_series || []).map((s) => {
              const stroke = s.delta > 0.5 ? "#81B29A" : s.delta < -0.5 ? "#E07A5F" : "#7A7D96";
              const chartData = (s.points || []).map((p) => ({ score: p.score, at: p.created_at }));
              return (
                <div key={s.user_id} className={`rounded-2xl border p-3 ${(s.milestones || []).length ? "border-secondary/40 bg-gradient-to-br from-secondary/5 to-white" : "border-[#3D405B]/10 bg-white"}`} data-testid={`wellness-client-${s.user_id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-bold text-[#3D405B] text-sm truncate">{s.name}</div>
                    <div className={`text-xs font-extrabold ${s.delta > 0.5 ? "text-secondary" : s.delta < -0.5 ? "text-destructive" : "text-[#7A7D96]"}`}>
                      {s.delta > 0 ? "+" : ""}{s.delta}
                    </div>
                  </div>
                  <div className="text-[10px] text-[#7A7D96] mb-1">{s.first} → {s.last} · {(s.points || []).length} check-ins</div>
                  {(s.milestones || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2" data-testid={`wellness-client-milestones-${s.user_id}`}>
                      {s.milestones.map((key) => {
                        const m = MILESTONES[key];
                        if (!m) return null;
                        const Icon = m.icon;
                        return (
                          <span key={key} className={`inline-flex items-center gap-1 pill text-[10px] border ${m.tone}`} title={m.label} data-testid={`wellness-badge-${key}-${s.user_id}`}>
                            <Icon size={10} /> {m.label}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  <div style={{ width: "100%", height: 60 }}>
                    <ResponsiveContainer>
                      <LineChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                        <YAxis domain={[1, 10]} hide />
                        <Tooltip
                          formatter={(v) => [`score ${v}`, ""]}
                          labelFormatter={(_, payload) => payload?.[0]?.payload?.at ? new Date(payload[0].payload.at).toLocaleDateString() : ""}
                          contentStyle={{ borderRadius: 10, border: "1px solid #3D405B22", fontSize: 11 }}
                        />
                        <Line type="monotone" dataKey="score" stroke={stroke} strokeWidth={2} dot={{ r: 2 }} isAnimationActive={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}

// ---------- Report c. Utilization ----------
export function UtilizationReport() {
  const [days, setDays] = useState(30);
  const { data, loading } = useReport(`/admin/reports/utilization?days_back=${days}`, [days]);
  if (loading) return <div className="brand-card p-8 animate-pulse h-40" />;
  if (!data) return null;
  const t = data.totals || {};
  return (
    <div className="space-y-4" data-testid="report-utilization">
      <WindowSelect value={days} onChange={setDays} options={[7, 30, 60, 90]} testid="utilization-window" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Slots published" value={t.published} testid="util-published" />
        <Stat label="Slots booked" value={t.booked} tone="secondary" testid="util-booked" />
        <Stat label="Fill rate" value={`${t.fill_rate_pct || 0}%`} tone={t.fill_rate_pct >= 60 ? "secondary" : t.fill_rate_pct >= 30 ? "default" : "danger"} testid="util-fill-rate" />
        <Stat label="Counsellors" value={t.counsellors} tone="muted" />
      </div>
      <Card testid="utilization-list">
        <div className="flex items-center gap-2 mb-3"><Gauge size={16} className="text-primary" /><div className="font-extrabold text-[#3D405B]">Per-counsellor utilization</div></div>
        <div className="divide-y divide-[#3D405B]/5">
          {(data.counsellors || []).map((c) => {
            const tone = c.utilization_pct >= 80 ? "text-destructive" : c.utilization_pct >= 50 ? "text-secondary" : c.utilization_pct >= 25 ? "text-[#3D405B]" : "text-[#7A7D96]";
            return (
              <div key={c.user_id} className="py-2 flex items-center gap-3 text-sm" data-testid={`util-row-${c.user_id}`}>
                <div className="flex-1 truncate font-bold text-[#3D405B]">{c.name}</div>
                <div className="w-32 h-2 rounded-full bg-muted/40 overflow-hidden">
                  <div className="h-full bg-primary" style={{ width: `${Math.min(100, c.utilization_pct)}%` }} />
                </div>
                <div className={`w-16 text-right font-extrabold ${tone}`}>{c.utilization_pct}%</div>
                <div className="w-24 text-right text-xs text-[#7A7D96]">{c.booked}/{c.published}</div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

// ---------- Report d. Funnel ----------
export function FunnelReport() {
  const [days, setDays] = useState(90);
  const { data, loading } = useReport(`/admin/reports/funnel?days_back=${days}`, [days]);
  if (loading) return <div className="brand-card p-8 animate-pulse h-32" />;
  if (!data) return null;
  const max = Math.max(1, ...(data.stages || []).map((s) => s.value));
  return (
    <div className="space-y-4" data-testid="report-funnel">
      <WindowSelect value={days} onChange={setDays} options={[30, 90, 180, 365]} testid="funnel-window" />
      <Card>
        <div className="flex items-center gap-2 mb-4"><Filter size={16} className="text-primary" /><div className="font-extrabold text-[#3D405B]">Client acquisition funnel</div></div>
        <div className="space-y-3">
          {(data.stages || []).map((s) => {
            const w = (s.value / max) * 100;
            return (
              <div key={s.label} className="space-y-1" data-testid={`funnel-stage-${s.label}`}>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-bold text-[#3D405B]">{s.label}</span>
                  <span className="text-[#5F627D]">
                    <span className="font-extrabold text-[#3D405B] text-lg">{s.value}</span>
                    {s.conversion_from_prev !== null && s.conversion_from_prev !== undefined && (
                      <span className="ml-2 text-xs">({s.conversion_from_prev}% conv.)</span>
                    )}
                  </span>
                </div>
                <div className="h-4 rounded-full bg-muted/40 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-primary to-secondary" style={{ width: `${w}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

// ---------- Report e. Response Times SLA ----------
export function ResponseTimesReport() {
  const [days, setDays] = useState(30);
  const { data, loading } = useReport(`/admin/reports/response-times?days_back=${days}`, [days]);
  if (loading) return <div className="brand-card p-8 animate-pulse h-32" />;
  if (!data) return null;
  return (
    <div className="space-y-4" data-testid="report-response-times">
      <WindowSelect value={days} onChange={setDays} options={[7, 30, 90]} testid="rt-window" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card testid="rt-accept">
          <div className="flex items-center gap-2 mb-3"><Timer size={16} className="text-primary" /><div className="font-extrabold text-[#3D405B]">Booking → counsellor accept</div></div>
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Median" value={fmtSeconds(data.booking_to_accept?.p50_seconds)} tone="secondary" />
            <Stat label="P90" value={fmtSeconds(data.booking_to_accept?.p90_seconds)} tone="default" />
            <Stat label="Samples" value={data.booking_to_accept?.count} tone="muted" />
          </div>
        </Card>
        <Card testid="rt-start">
          <div className="flex items-center gap-2 mb-3"><Timer size={16} className="text-secondary" /><div className="font-extrabold text-[#3D405B]">Waiting → counsellor start</div></div>
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Median" value={fmtSeconds(data.waiting_to_start?.p50_seconds)} tone="secondary" />
            <Stat label="P90" value={fmtSeconds(data.waiting_to_start?.p90_seconds)} tone="default" />
            <Stat label="Samples" value={data.waiting_to_start?.count} tone="muted" />
          </div>
        </Card>
      </div>
    </div>
  );
}

// ---------- Report f. Notes Compliance ----------
export function NotesComplianceReport() {
  const [days, setDays] = useState(30);
  const { data, loading } = useReport(`/admin/reports/notes-compliance?days_back=${days}`, [days]);
  if (loading) return <div className="brand-card p-8 animate-pulse h-32" />;
  if (!data) return null;
  const t = data.totals || {};
  return (
    <div className="space-y-4" data-testid="report-notes">
      <WindowSelect value={days} onChange={setDays} options={[7, 30, 90]} testid="notes-window" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Completed sessions" value={t.completed_sessions} testid="notes-total" />
        <Stat label="Filed on time (<48h)" value={t.notes_on_time} tone="secondary" testid="notes-on-time" />
        <Stat label="Filed late" value={t.notes_late} tone="default" testid="notes-late" />
        <Stat label="Missing" value={t.notes_missing} tone="danger" testid="notes-missing" />
      </div>
      <Card testid="notes-compliance">
        <div className="flex items-center gap-2 mb-3"><FileCheck2 size={16} className="text-primary" /><div className="font-extrabold text-[#3D405B]">Compliance overview</div></div>
        <div className="text-5xl font-extrabold text-[#3D405B]">{t.compliance_pct || 0}%</div>
        <div className="text-xs text-[#7A7D96] mt-1">of completed sessions have a DAP note filed within 48h</div>
        {(data.counsellors || []).length > 0 && (
          <div className="divide-y divide-[#3D405B]/5 mt-4">
            {(data.counsellors || []).map((c) => (
              <div key={c.user_id} className="py-2 flex items-center gap-3 text-sm" data-testid={`notes-row-${c.user_id}`}>
                <div className="flex-1 truncate font-bold text-[#3D405B]">{c.name || c.user_id}</div>
                <div className="w-32 h-2 rounded-full bg-muted/40 overflow-hidden">
                  <div className={`h-full ${c.compliance_pct >= 80 ? "bg-secondary" : c.compliance_pct >= 50 ? "bg-primary" : "bg-destructive"}`} style={{ width: `${c.compliance_pct}%` }} />
                </div>
                <div className={`w-14 text-right font-extrabold ${c.compliance_pct >= 80 ? "text-secondary" : c.compliance_pct >= 50 ? "text-[#3D405B]" : "text-destructive"}`}>{c.compliance_pct}%</div>
                <div className="w-24 text-right text-xs text-[#7A7D96]">{c.on_time}/{c.total}</div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// ---------- Report g. Risk & Escalation ----------
export function RiskRegisterReport() {
  const [days, setDays] = useState(60);
  const { data, loading } = useReport(`/admin/reports/risk-register?days_back=${days}`, [days]);
  if (loading) return <div className="brand-card p-8 animate-pulse h-32" />;
  if (!data) return null;
  const t = data.totals || {};
  return (
    <div className="space-y-4" data-testid="report-risk">
      <WindowSelect value={days} onChange={setDays} options={[30, 60, 90, 180]} testid="risk-window" />
      <div className="grid grid-cols-2 gap-3">
        <Stat label="Flagged case notes" value={t.risk_notes} tone="danger" testid="risk-notes-count" />
        <Stat label="Low wellness check-ins (≤3)" value={t.low_wellness} tone="danger" testid="risk-low-wellness" />
      </div>
      {(data.risk_notes || []).length > 0 && (
        <Card testid="risk-notes-list">
          <div className="flex items-center gap-2 mb-3"><ShieldAlert size={16} className="text-destructive" /><div className="font-extrabold text-[#3D405B]">Flagged case notes</div></div>
          <div className="divide-y divide-[#3D405B]/5">
            {(data.risk_notes || []).map((n) => (
              <div key={n.note_id || n.created_at} className="py-2 text-sm text-[#3D405B] flex flex-wrap items-center gap-2" data-testid={`risk-note-row-${n.note_id || n.created_at}`}>
                <span className="pill bg-destructive/10 text-destructive">Escalation L{n.escalation_level || 1}</span>
                <span className="font-bold">{n.client?.name || "?"}</span>
                <span className="text-xs text-[#7A7D96]">{new Date(n.created_at).toLocaleString()}</span>
                {n.summary && <span className="text-xs text-[#5F627D] italic">&ldquo;{(n.summary || "").slice(0, 120)}&rdquo;</span>}
              </div>
            ))}
          </div>
        </Card>
      )}
      {(data.low_wellness_checkins || []).length > 0 && (
        <Card testid="risk-wellness-list">
          <div className="font-extrabold text-[#3D405B] mb-3">Low wellness check-ins (score ≤ 3)</div>
          <div className="divide-y divide-[#3D405B]/5">
            {(data.low_wellness_checkins || []).map((c) => (
              <div key={c.checkin_id || c.created_at} className="py-2 text-sm text-[#3D405B] flex flex-wrap items-center gap-2">
                <span className="pill bg-destructive/10 text-destructive">score {c.score}</span>
                <span className="font-bold">{c.user?.name || "?"}</span>
                <span className="text-xs text-[#7A7D96]">{new Date(c.created_at).toLocaleString()}</span>
                {c.mood_note && <span className="text-xs text-[#5F627D] italic">&ldquo;{(c.mood_note || "").slice(0, 120)}&rdquo;</span>}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

// ---------- Report h. Donations Ledger ----------
export function DonationsReport() {
  const [days, setDays] = useState(90);
  const { data, loading } = useReport(`/admin/reports/donations?days_back=${days}`, [days]);
  if (loading) return <div className="brand-card p-8 animate-pulse h-32" />;
  if (!data) return null;
  const t = data.totals || {};
  const currency = Object.keys(t.by_currency || { ZAR: 0 })[0] || "ZAR";
  return (
    <div className="space-y-4" data-testid="report-donations">
      <WindowSelect value={days} onChange={setDays} options={[30, 90, 180, 365]} testid="donations-window" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Total donations" value={fmtAmount(t.amount_minor_units, currency)} tone="secondary" testid="donations-total" />
        <Stat label="Number of donations" value={t.donations_count} testid="donations-count" />
        <Stat label="Sessions sponsored" value={t.sessions_sponsored} tone="primary" testid="donations-sponsored" />
        <Stat label="Currencies" value={Object.keys(t.by_currency || {}).length} tone="muted" />
      </div>
      <Card testid="donations-by-campaign">
        <div className="flex items-center gap-2 mb-3"><Coins size={16} className="text-secondary" /><div className="font-extrabold text-[#3D405B]">By campaign</div></div>
        {(data.by_campaign || []).length === 0 ? (
          <div className="text-sm text-[#7A7D96]">No donations recorded yet in this window.</div>
        ) : (data.by_campaign || []).map((c) => (
          <div key={c.campaign} className="flex justify-between py-1 text-sm">
            <span className="text-[#5F627D]">{c.campaign.replace(/_/g, " ")}</span>
            <span className="font-extrabold text-[#3D405B]">{fmtAmount(c.amount, currency)}</span>
          </div>
        ))}
      </Card>
      {(data.rows || []).length > 0 && (
        <Card>
          <div className="font-extrabold text-[#3D405B] mb-3">Recent ledger (latest 100)</div>
          <div className="divide-y divide-[#3D405B]/5 max-h-80 overflow-y-auto">
            {(data.rows || []).map((r) => (
              <div key={r.donation_id || r.id || r.created_at} className="py-2 text-sm flex flex-wrap items-center gap-2">
                <span className="font-extrabold text-[#3D405B]">{fmtAmount(r.amount, r.currency)}</span>
                <span className="text-[#5F627D]">{r.donor_name || r.donor_email || "Anonymous"}</span>
                {r.campaign && <span className="pill bg-primary/10 text-primary">{r.campaign.replace(/_/g, " ")}</span>}
                <span className="text-xs text-[#7A7D96]">{new Date(r.created_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

// ---------- Shared window selector ----------
function WindowSelect({ value, onChange, options, testid }) {
  return (
    <div className="flex items-center gap-3">
      <label className="text-[11px] uppercase font-extrabold tracking-[0.14em] text-[#3D405B]">Window</label>
      <select
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="text-sm rounded-xl border border-[#3D405B]/15 bg-white px-3 py-1.5 font-semibold text-[#3D405B]"
        data-testid={testid}
      >
        {options.map((n) => <option key={n} value={n}>{n === 7 ? "Last 7 days" : n === 30 ? "Last 30 days" : n === 60 ? "Last 60 days" : n === 90 ? "Last 90 days" : n === 180 ? "Last 6 months" : n === 365 ? "Last 12 months" : `Last ${n} days`}</option>)}
      </select>
    </div>
  );
}
